import { zodResolver } from '@hookform/resolvers/zod';
import { isEmpty } from 'rambda';
import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { useTypedParams } from 'react-router-typesafe-routes';
import { z } from 'zod';
import FormInput from '@/components/common/form-input';
import ProgressButton from '@/components/common/progress-button';
import PageLayout from '@/components/layouts/page-layout/page-layout';
import LoadingLayout from '@/components/layouts/progress-layout/loading-layout';
import useAdjustLayout from '@/hooks/use-adjust-layout';
import useFriendInfo from '@/hooks/users/use-friend-info';
import useUpdateFriendRemark from '@/hooks/users/use-update-friend-remark';
import type { RespondedType } from '@/models/response';
import ROUTES from '@/routes';

export default function UpdateUserRemarkRoute() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { userId: friendId } = useTypedParams(ROUTES.CONTACT.USERS.BY_ID);

  const { friendInfo, isLoading } = useFriendInfo(friendId);
  const { mutateAsync: updateFriendRemark } = useUpdateFriendRemark(friendId);

  const { control, handleSubmit, resetField } = useForm<UpdateRemarkFormData>({
    resolver: zodResolver(updateRemarkFormScheme),
    defaultValues: {
      nickname: '',
    },
  });

  useEffect(() => {
    if (friendInfo?.FriendInfo) {
      const { RemarkName = '', FriendNickName = '' } = friendInfo.FriendInfo;
      if (RemarkName !== FriendNickName) resetField('nickname', { defaultValue: RemarkName });
    }
  }, [friendInfo?.FriendInfo, resetField]);

  useAdjustLayout();

  const submit = async (data: UpdateRemarkFormData) => {
    let result: RespondedType;

    if (data.nickname === friendInfo?.FriendInfo?.RemarkName) {
      result = { Code: 0, Msg: '' };
    } else if (isEmpty(data.nickname) || data.nickname === friendInfo?.FriendInfo?.FriendNickName) {
      result = await updateFriendRemark('');
    } else {
      result = await updateFriendRemark(data.nickname);
    }

    if (result.Code === 0) {
      navigate(-1);
    }
  };

  return (
    <PageLayout
      title={t('user.remark.name')}
      headerConfig={{
        navigateBack: true,
      }}>
      <LoadingLayout promise={isLoading}>
        {() => (
          <form
            onSubmit={handleSubmit(submit)}
            className='mx-5 pb-6 flex flex-1 flex-col justify-between'>
            <FormInput
              disabled={isLoading}
              name='nickname'
              placeholder={friendInfo?.FriendInfo.FriendNickName}
              control={control}
              showClearButton
            />

            <ProgressButton
              as='submit'
              isLoading={isLoading}
              className='btn btn-primary'
              label='common.confirm'
            />
          </form>
        )}
      </LoadingLayout>
    </PageLayout>
  );
}

const updateRemarkFormScheme = z.object({
  nickname: z.string().trim().max(50, 'errors.string.max-length|count=50'),
});

type UpdateRemarkFormData = z.infer<typeof updateRemarkFormScheme>;
